import { useEffect, useState } from "react";
import InfoIconWithTooltip from "src/common/components/Icon/InfoIconWithTooltip";
import { TruncatedText } from "../../../../common/components/TruncatedText/TruncatedText";

type OptionSectionProps = {
  id: string;
  title: string;
  enabled: boolean;
  onToggleEnabled: () => void;
  summary: string;
  children: React.ReactNode;
  infoTooltip?: string;
} & (
  | { open?: never; onOpenChange?: never }
  | { open: boolean; onOpenChange: (open: boolean) => void }
);

const OptionSection = ({
  id,
  title,
  enabled,
  onToggleEnabled,
  summary,
  children,
  infoTooltip,
  open: openProp,
  onOpenChange,
}: OptionSectionProps) => {
  const controlled = openProp !== undefined;
  const [openInternal, setOpenInternal] = useState(enabled);
  const open = openProp ?? openInternal;

  useEffect(() => {
    if (!controlled) setOpenInternal(enabled);
  }, [enabled, controlled]);

  const handleToggle = () => {
    if (controlled) {
      onOpenChange?.(!open);
    } else {
      setOpenInternal((v) => !v);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <input
          type="checkbox"
          id={`${id}-toggle`}
          checked={enabled}
          onChange={onToggleEnabled}
          className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-green-600"
        />
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={handleToggle}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <span className="text-sm font-medium text-gray-700">{title}</span>
          <TruncatedText className="flex-1 text-xs text-gray-500" text={summary} />
          <span className="shrink-0 text-gray-400 text-[10px] w-3" aria-hidden>
            <span className={`inline-block ${open ? "" : "-rotate-90"}`}>▾</span>
          </span>
        </button>
        {infoTooltip && <InfoIconWithTooltip message={infoTooltip} />}
      </div>
      {open && (
        <div id={id} className="px-3 pb-3 pt-1 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

export default OptionSection;
